import { useCompany } from "@/contexts/CompanyContext";
import { useDirtyNav } from "@/contexts/DirtyNavContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Briefcase, ChevronDown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const WorkspaceSwitcher = () => {
  const { workspaces, currentWorkspace, selectWorkspace } = useCompany();
  const { confirm } = useDirtyNav();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (workspaces.length <= 1 || !currentWorkspace) return null;

  const labelFor = (w: { name: string; primaryCompanyName?: string | null }) =>
    w.primaryCompanyName || w.name;

  // Same isolation guarantee as CompanySwitcher: confirm unsaved changes first,
  // then jump to /dashboard so the user never lands on a record from another tenant.
  const handleSelect = async (id: string) => {
    if (id === currentWorkspace.id) return;
    const ok = await confirm();
    if (!ok) return;
    await selectWorkspace(id);
    navigate("/dashboard", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-1.5 sm:gap-2 min-w-0 max-w-[38vw] sm:max-w-[220px] px-2 sm:px-3">
          <Briefcase className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate text-sm">{labelFor(currentWorkspace)}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{t("workspaces.switchSubscription")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onClick={() => handleSelect(w.id)} className="flex justify-between gap-2">
            <span className="truncate flex-1">{labelFor(w)}</span>
            <span className="text-xs text-muted-foreground">{t(`workspaces.role.${w.role}`)}</span>
            {w.id === currentWorkspace.id && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
